import { ProfileTypes, usersRef, UserType } from '@api/Users/User';
import { doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import store from '@store/index';
import { auth } from '@api/Firebase/Firebase';
import BranchOfficeServ from '@api/BranchOffices/BranchOfficeServ';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { getBase64, getBase64Extension, getBase64MimeType } from '@utils/Base64';
import StorageServ from '@api/Firebase/StorageService/StorageServ';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeUserStatus } from '@api/ActivityLogs/ConfigActivity';
import { getCurrentPermissionSubject } from '@utils/Permissions';
import { canManageUser, validateUserSave } from '@utils/Permissions/Capabilities';

export interface PersonalDataType {
    firstname: string;
    lastname: string;
    email: string;
    address: string;
    phone: string;
}

export interface UpdatePinType {
    oldPin: string;
    newPin: string;
    confirmNewPin: string;
}

const USERS_PATH = 'users/';

// Caché de docs de `users` — ver el comentario de getUser. Vive lo que viva la
// pestaña; el TTL corto es solo para que un cambio de nombre no tarde en verse.
const USER_CACHE_MS = 10 * 60_000;
const userCache = new Map<string, { user: UserType; at: number }>();
const userInflight = new Map<string, Promise<UserType>>();
export const invalidateUserCache = (userId?: string) => {
    if (userId) userCache.delete(userId);
    else userCache.clear();
};

export default {
    /**
     * El gate del alta y la edición (plan de Gastos F6): nadie asigna más poder del
     * que tiene ni sucursales que no son suyas, y al editar, el usuario tiene que ser
     * suyo. Va aquí y no en el modal: un gate va sobre la función que ejecuta.
     */
    async assertCanSaveUser(values: { uid?: string; profile: string; branchOffices: string[] }): Promise<void> {
        const previous = values.uid ? await this.getUser(values.uid) : undefined;
        const problem = validateUserSave(
            getCurrentPermissionSubject(),
            { profile: values.profile as ProfileTypes, branchOffices: values.branchOffices },
            previous?.profile
        );
        if (problem) throw new Error(problem);
    },
    async getUsers(filters?: any, excludeCurrentAccount?: boolean): Promise<UserType[]> {
        const users: UserType[] = [];
        const filtersQuery = [];
        if (excludeCurrentAccount) {
            const userId = this.getUserId();
            filtersQuery.push(where('uid', '!=', userId));
        }
        if (filters?.profile !== 'all') {
            filtersQuery.push(where('profile.profile', '==', filters.profile));
        }
        if (filters?.branchOffice !== 'all') {
            filtersQuery.push(where('profile.branchOffices', 'array-contains', filters.branchOffice));
        }
        const snapshot = await getDocs(query(usersRef, ...filtersQuery));
        if (snapshot.empty) {
            return users;
        }
        snapshot.forEach((doc: any) => {
            users.push(doc.data() as UserType);
        });
        return users;
    },
    async getUsersByProfile(profile: ProfileTypes): Promise<UserType[]> {
        const users: UserType[] = [];
        const queryDoc = query(usersRef, where('profile.profile', '==', profile));
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return users;
        }
        snapshot.forEach((doc: any) => {
            users.push(doc.data() as UserType);
        });
        return users;
    },
    /**
     * Activa o da de baja a un usuario del staff. **Es control de acceso**, así
     * que se registra diciendo a QUIÉN se le abrió o se le cerró la puerta
     * (M5 de la bitácora, 2026-09-11) — un `true → false` no sirve para auditar.
     */
    async toggleStatus(userId: string): Promise<void> {
        const user = await this.getUser(userId);
        if (!canManageUser(getCurrentPermissionSubject(), user.profile)) {
            throw new Error('Este usuario no es de tus sucursales');
        }
        const status = !user.profile.status;
        await updateDoc(doc(usersRef, userId), {
            'profile.status': status
        });
        invalidateUserCache(userId);
        try {
            await ActivityLogServ.addActivityLog(
                'user',
                userId,
                describeUserStatus(user.profile?.name || 'un usuario', status)
            );
        } catch (error) {
            console.error('No se pudo registrar el cambio de acceso del usuario', error);
        }
        return;
    },
    async updatePersonalData(personalDataType: PersonalDataType): Promise<UserType> {
        const userId = this.getUserId();
        await updateDoc(doc(usersRef, userId), {
            'profile.firstname': personalDataType.firstname,
            'profile.lastname': personalDataType.lastname,
            'profile.address': personalDataType.address,
            'profile.phone': personalDataType.phone
        });
        invalidateUserCache(userId);
        return await this.getUser();
    },
    async updatePin(updatePin: UpdatePinType): Promise<void> {
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
            // `credential()` es SÍNCRONO en Firebase v9: el `await` que tenía no esperaba nada.
            const credentials = EmailAuthProvider.credential(currentUser.email, updatePin.oldPin);
            await reauthenticateWithCredential(currentUser, credentials);
            await updatePassword(currentUser, updatePin.newPin);
        } else {
            throw new Error('User not logged');
        }
        return;
    },
    async getMainBranchOffice(id?: string): Promise<BranchOffice> {
        const user = await this.getUser(id);
        if (user.profile.branchOffices[0] === 'all') {
            const branchOffices = await BranchOfficeServ.getBranchOffices();
            return branchOffices[0];
        }
        return await BranchOfficeServ.getBranchOffice(user.profile.branchOffices[0]);
    },
    async uploadProfilePhoto(photoBase64: string): Promise<string> {
        const userId = this.getUserId();
        const path = `${USERS_PATH}${userId}.${getBase64Extension(photoBase64)}`;
        const url = await StorageServ.uploadFileToStorage(path, getBase64(photoBase64), getBase64MimeType(photoBase64));
        await updateDoc(doc(usersRef, userId), {
            'profile.photo': url
        });
        invalidateUserCache(userId);
        return url;
    },
    /**
     * ⚠️ CON CACHÉ, y no es optimización de adorno: este getUser se llama **por
     * PEDIDO** en `getOrders` (el N+1 del Dashboard: 7,857 lecturas por carga) y
     * —lo que encendió la factura— dentro del **onSnapshot del tablero de
     * cocina** (`getOrdersRealtime`): en CADA entrega del snapshot se releía el
     * usuario de TODOS los pedidos de la ventana, en cada tablet, todo el día.
     * Medido el 2026-09-04: **386,000 lecturas en UN día**, cuando el sistema
     * entero ronda las decenas de miles. El staff son ~42 docs que casi nunca
     * cambian: releerlos cientos de miles de veces era la factura, no el dato.
     *
     * Diseño: caché por uid con TTL de 10 min + deduplicación de vuelos (60
     * llamadas concurrentes al mismo uid = UNA lectura). También se cachea el
     * "no existe": un colaborador borrado no puede volverse una relectura
     * eterna. Los mutadores de abajo invalidan su entrada.
     */
    async getUser(id?: string): Promise<UserType> {
        const userId = id || this.getUserId();
        const cached = userCache.get(userId);
        if (cached && Date.now() - cached.at < USER_CACHE_MS) return cached.user;
        const pending = userInflight.get(userId);
        if (pending) return pending;
        const promise = (async () => {
            const userDocument = await getDoc(doc(usersRef, userId));
            const user = userDocument.data() as UserType;
            userCache.set(userId, { user, at: Date.now() });
            return user;
        })().finally(() => userInflight.delete(userId));
        userInflight.set(userId, promise);
        return promise;
    },
    getUserId(): string {
        let loggedUser = store.getState().auth.loggedUser;
        if (!loggedUser) {
            loggedUser = auth.currentUser as any;
            if (!loggedUser) {
                throw new Error('User not logged');
            }
        }
        return loggedUser.uid;
    }
};
